import { useEffect } from 'react';

export interface SeoMetaTag {
  name: string;
  content: string;
}

/**
 * Page-level <head> tags — mirrors Flutter's `seo` package `Seo.head(tags: [MetaTag(...)])` and,
 * with `title`, Flutter's `Title` widget. While mounted, each `<meta name>` gets the given content
 * (created when index.html has none) and the document title is replaced; on unmount the previous
 * values come back, as the seo package drops a page's tags when it leaves the tree.
 */
export function SeoHead({ title, meta = [] }: { title?: string; meta?: SeoMetaTag[] }) {
  const metaKey = JSON.stringify(meta);

  useEffect(() => {
    const tags = JSON.parse(metaKey) as SeoMetaTag[];
    const restores: (() => void)[] = [];

    for (const { name, content } of tags) {
      const existing = Array.from(document.head.querySelectorAll('meta')).find(
        (el) => el.getAttribute('name') === name,
      );
      if (existing) {
        const previous = existing.getAttribute('content');
        existing.setAttribute('content', content);
        restores.push(() => {
          if (previous === null) existing.removeAttribute('content');
          else existing.setAttribute('content', previous);
        });
      } else {
        const created = document.createElement('meta');
        created.setAttribute('name', name);
        created.setAttribute('content', content);
        document.head.appendChild(created);
        restores.push(() => created.remove());
      }
    }

    if (title !== undefined) {
      const previousTitle = document.title;
      document.title = title;
      restores.push(() => { document.title = previousTitle; });
    }

    return () => restores.reverse().forEach((restore) => restore());
  }, [title, metaKey]);

  return null;
}
